import React, { useState, useEffect } from 'react';
import { Activity, Dumbbell, Timer, TrendingUp, Target, Brain } from 'lucide-react';
import { StrainChart } from '../strain/StrainChart';
import { StrainBreakdown } from '../strain/StrainBreakdown';
import { StrainInsights } from '../strain/StrainInsights';
import { StrainZones } from '../strain/StrainZones';
import { StrainKnowledge } from '../strain/StrainKnowledge';
import { HealthMetrics } from '../../types/health';
import { StrainMetricCard } from '../strain/StrainMetricCard';

interface StrainTabProps {
  metrics: HealthMetrics;
  onClose: () => void;
}

export function StrainTab({ metrics, onClose }: StrainTabProps) {
  const [selectedTimeframe, setSelectedTimeframe] = useState<'24h' | '7d' | '30d'>('24h');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  return (
    <div className="space-y-6">
      {/* Strain Metrics Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StrainMetricCard
          title="Day Strain"
          value={metrics.strain.current}
          icon={<Activity className="w-4 h-4" />}
          status={metrics.strain.current > metrics.strain.optimal.max ? 'warning' : 'success'}
          comparison={`${metrics.strain.change > 0 ? '+' : ''}${metrics.strain.change.toFixed(1)} vs yesterday`}
        />
        <StrainMetricCard
          title="Activity Minutes"
          value={145}
          unit="min"
          icon={<Timer className="w-4 h-4" />}
          status="success"
          comparison="+15min vs baseline"
        />
        <StrainMetricCard
          title="Peak HR"
          value={172}
          unit="bpm"
          icon={<Target className="w-4 h-4" />}
          status="success"
          comparison="Zone 4 achieved"
        />
        <StrainMetricCard
          title="Recovery Required"
          value={8.5}
          unit="hours"
          icon={<Brain className="w-4 h-4" />}
          status="warning"
          comparison="Above baseline"
        />
      </div>

      {/* Strain Chart */}
      <div className="theme-card p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">Strain Analysis</h3>
          <div className="flex gap-2">
            {(['24h', '7d', '30d'] as const).map((timeframe) => (
              <button
                key={timeframe}
                onClick={() => setSelectedTimeframe(timeframe)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  selectedTimeframe === timeframe
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/70'
                }`}
              >
                {timeframe}
              </button>
            ))}
          </div>
        </div>
        <StrainChart 
          data={metrics.strain.dayHistory}
          timeframe={selectedTimeframe}
        />
      </div>

      {/* Strain Zones */}
      <StrainZones metrics={metrics} />

      {/* Activity Breakdown */}
      <StrainBreakdown metrics={metrics} />

      {/* ML-Powered Insights */}
      <StrainInsights metrics={metrics} />

      {/* Knowledge Center */}
      <StrainKnowledge />
    </div>
  );
}